// src/services/fishIQLocationRanker.ts
// FishIQ location ranker.
// Keeps FishIQ species-guide-first, then uses water/region/season/local signals
// to sort the catalog for the current Big Blue / FishIQ context.

import type { LogEntry } from '../types';
import {
  fishIQSpeciesAllowedForAuthoritativeRegion,
  getFishIQRegionalSpeciesSignal,
  type FishIQRegionalProfileContext,
  type FishIQRegionalSpeciesSignal,
} from '../constants/fishIQRegionalProfiles';
import { fishIQSpeciesAllowedForWaterContext, resolveWaterContext } from './fishIQWaterContext';
import { getCanonicalFishIQMetadata } from '../data/fishIQCanonicalSpecies';
import { findFishIQWaterbodyProfile, getFishIQWaterbodySpeciesSignal } from '../data/fishIQWaterbodyProfiles';
import {
  applyFishIQCatchabilityFacts,
  getFishIQCatchabilityGeoAdjustment,
} from '../data/fishIQCatchabilityFacts';

export type FishIQLocationRankBucket =
  | 'likely'
  | 'good_target'
  | 'possible'
  | 'seasonal'
  | 'stocked'
  | 'quiet';

export type FishIQLocationContext = {
  lat?: number | null;
  lon?: number | null;
  waterName?: string | null;
  waterType?: string | null;
  waterKind?: string | null;
  regionHint?: string | null;
  season?: string | null;
  timeOfDay?: string | null;
  source?: string | null;
};

export type FishIQLocationRank = {
  score: number;
  bucket: FishIQLocationRankBucket;
  label: string;
  shortLabel: string;
  reasons: string[];
  confidenceTone: 'profile' | 'range' | 'seasonal' | 'local' | 'stocked' | 'quiet';
  bestWindow?: string;
};

export type FishIQRankedSpecies<T = any> = T & {
  __fishiq?: {
    locationRank?: FishIQLocationRank;
    whyHere?: string;
    whyNow?: string;
    bestMove?: string;
    bestBait?: string;
    sourceLabel?: string;
    supportingReads?: string[];
    regionalSignal?: FishIQRegionalSpeciesSignal | null;
    waterbodyProfile?: { id: string; name: string; status: string } | null;
    canonicalDataQuality?: {
      hasScientificName: boolean;
      hasWaterRealm: boolean;
      hasHabitat: boolean;
      hasRegion: boolean;
      hasSource: boolean;
      completeness: number;
    } | null;
    environmentalSignal?: {
      confidence: number;
      scoreDelta: number;
      reasons: string[];
      whyNow?: string;
      bestMove?: string;
      supportingReads?: string[];
      compatible: boolean;
      currentCompatible: boolean;
      bathymetryCompatible: boolean;
      compatibilityReason?: string;
    } | null;
  };
};

export type FishIQLocationRankOptions = {
  signals?: any[];
  catchLog?: LogEntry[] | any[];
  query?: string;
  environment?: any | null;

  /**
   * Generalized map/water features used to support habitat-aware ranking.
   * Examples: lake, river, bay, harbor, reservoir, shoreline, wetland,
   * structure, vegetation, or a generated map-area hotspot.
   */
  waterFeatures?: any[] | null;
};

const clean = (value: any) => String(value ?? '').trim();
const lower = (value: any) => clean(value).toLowerCase();

const unique = <T,>(items: T[]): T[] => {
  const out: T[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const key = lower(item);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
};

const humanize = (value: any) =>
  clean(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());

const currentSeason = (): string => {
  const month = new Date().getMonth() + 1;
  if (month >= 3 && month <= 5) return 'spring';
  if (month >= 6 && month <= 8) return 'summer';
  if (month >= 9 && month <= 11) return 'fall';
  return 'winter';
};

const currentTimeBucket = (): string => {
  const hour = new Date().getHours();
  if (hour >= 4 && hour < 9) return 'dawn';
  if (hour >= 9 && hour < 17) return 'day';
  if (hour >= 17 && hour < 21) return 'dusk';
  return 'night';
};

const usesRegionalClimateSeason = (ctx: FishIQLocationContext): boolean => {
  const hay = lower([ctx.regionHint, ctx.waterName].filter(Boolean).join(' '));
  const lat = Number(ctx.lat);
  const lon = Number(ctx.lon);
  if (/philippines|indonesia|india|luzon|visayas|mindanao|java|sumatra|kalimantan|sulawesi|bali|ganga|ganges|brahmaputra|arabian sea|bay of bengal/.test(hay)) return true;
  if (Number.isFinite(lat) && Number.isFinite(lon)) {
    if (lat >= 4 && lat <= 21.5 && lon >= 116 && lon <= 127.5) return true;
    if (lat >= -11.5 && lat <= 6.5 && lon >= 94 && lon <= 142.5) return true;
    if (lat >= 6 && lat <= 35.8 && lon >= 68 && lon <= 97.5) return true;
  }
  return false;
};

const normalizeWaterType = (value: any): string => {
  const raw = lower(value);
  if (!raw) return '';
  if (raw.includes('reservoir')) return 'reservoir';
  if (raw.includes('pond')) return 'pond';
  if (raw.includes('creek') || raw.includes('stream') || raw.includes('brook')) return 'creek';
  if (raw.includes('river')) return 'river';
  if (raw.includes('lake')) return 'lake';
  if (raw.includes('bay')) return 'bay';
  if (raw.includes('harbor') || raw.includes('harbour') || raw.includes('marina')) return 'harbor';
  if (raw.includes('backwater')) return 'backwater';
  if (raw.includes('reef') || raw.includes('coral')) return 'reef';
  if (raw.includes('surf') || raw.includes('beach')) return 'surf';
  if (raw.includes('pier') || raw.includes('jetty') || raw.includes('breakwater')) return 'pier';
  if (raw.includes('estuary') || raw.includes('lagoon')) return 'estuary';
  if (raw.includes('marsh')) return 'marsh';
  if (raw.includes('canal')) return 'canal';
  if (raw.includes('ocean') || raw.includes('coast') || raw.includes('inshore') || raw.includes('salt')) return 'inshore';
  return raw;
};

const inferWaterKind = (
  waterType?: string | null,
  waterKind?: string | null,
  waterName?: string | null,
  lat?: number | null,
  lon?: number | null,
  regionHint?: string | null,
): string => {
  const resolved = resolveWaterContext({ lat, lon, waterName, waterKind, waterType, regionHint });

  // Shared guard: freshwater/inland signals win before broad coastal terms.
  // This keeps inland lakes/rivers/canals in coastal states and SEA countries
  // from ranking saltwater species.
  if (resolved.contextClass === 'freshwater' || resolved.contextClass === 'inland') return 'freshwater';
  if (/\b(brackish|estuary|lagoon|delta|tidal)\b/.test(resolved.haystack)) return 'brackish';
  if (resolved.contextClass === 'coastal' || resolved.contextClass === 'offshore') return 'saltwater';
  return waterKind || '';
};

export function buildFishIQLocationContext(input: Partial<FishIQLocationContext> = {}): FishIQLocationContext {
  const explicitType = normalizeWaterType(input.waterType);
  const typeIsRealmOnly = ['freshwater', 'fresh water', 'inland', 'saltwater', 'salt water', 'brackish', 'coastal', 'marine'].includes(explicitType);
  const namedType = normalizeWaterType(input.waterName);
  const kindType = normalizeWaterType(input.waterKind);

  // D.6.5.3: preserve a specific habitat when the caller gives both a broad
  // realm (`freshwater`) and a useful water name (`... lake`, `... reservoir`,
  // etc.). The regional authority gate now also supports realm-only values, but
  // carrying the best available habitat forward improves ranking/tactics too.
  const waterType = typeIsRealmOnly
    ? (namedType || kindType || explicitType)
    : (explicitType || kindType || namedType);
  const waterKind = inferWaterKind(
    waterType,
    input.waterKind,
    input.waterName,
    typeof input.lat === 'number' ? input.lat : input.lat == null ? null : Number(input.lat),
    typeof input.lon === 'number' ? input.lon : input.lon == null ? null : Number(input.lon),
    input.regionHint,
  );

  return {
    lat: typeof input.lat === 'number' ? input.lat : input.lat == null ? null : Number(input.lat),
    lon: typeof input.lon === 'number' ? input.lon : input.lon == null ? null : Number(input.lon),
    waterName: input.waterName || null,
    waterType: waterType || null,
    waterKind: waterKind || null,
    regionHint: input.regionHint || null,
    season: input.season || currentSeason(),
    timeOfDay: input.timeOfDay || currentTimeBucket(),
    source: input.source || null,
  };
}

const list = (value: any): any[] => (Array.isArray(value) ? value : value == null ? [] : [value]);

const getSpeciesKeys = (species: any): string[] => {
  const feeding = species?.feedingProfile || {};
  return unique([
    species?.id,
    species?.slug,
    species?.commonName,
    species?.name,
    species?.scientificName,
    species?.family,
    ...(species?.aliases || []),
    ...(species?.regions || []),
    ...(species?.habitats || []),
    ...(species?.behaviorTags || []),
    ...(species?.structureTags || []),
    ...(species?.keyTraits || []),
    ...(feeding?.primaryPrey || []),
    ...(feeding?.prey || []),
  ].map(lower));
};

const textForSpecies = (species: any): string => {
  const k = getSpeciesKeys(species);
  return k.join(' ');
};

const speciesName = (species: any) => clean(species?.commonName || species?.name || species?.id || 'Species');

const waterRealmFit = (species: any, ctx: FishIQLocationContext): boolean => {
  const kind = lower(ctx.waterKind);
  const category = lower(species?.category);
  const realms = list(species?.waterRealms).map(lower);
  const habitats = list(species?.habitats).map(lower).join(' ');

  if (!kind) return false;
  if (kind === 'brackish') {
    return category === 'both' || category === 'saltwater' || category === 'freshwater' || realms.includes('brackish') || /estuary|bay|river|inshore/.test(habitats);
  }
  if (kind === 'saltwater') {
    return category === 'saltwater' || category === 'both' || realms.includes('saltwater') || /inshore|bay|ocean|surf|estuary|harbor|pier|coast/.test(habitats);
  }
  if (kind === 'freshwater') {
    return category === 'freshwater' || category === 'both' || realms.includes('freshwater') || /lake|river|pond|creek|reservoir|stream/.test(habitats);
  }
  return false;
};

const waterTypeFit = (species: any, ctx: FishIQLocationContext): boolean => {
  const waterType = lower(ctx.waterType);
  if (!waterType) return false;
  const hay = textForSpecies(species);

  const groups: Record<string, string[]> = {
    lake: ['lake', 'reservoir', 'pond', 'bass', 'bluegill', 'crappie', 'walleye', 'trout', 'catfish', 'carp', 'perch', 'pike', 'muskie'],
    pond: ['pond', 'lake', 'bluegill', 'bass', 'catfish', 'crappie', 'carp', 'sunfish'],
    reservoir: ['reservoir', 'lake', 'bass', 'striper', 'striped bass', 'walleye', 'catfish', 'shad', 'crappie', 'trout'],
    river: ['river', 'current', 'smallmouth', 'trout', 'salmon', 'steelhead', 'catfish', 'carp', 'walleye', 'striper'],
    creek: ['creek', 'stream', 'brook', 'trout', 'smallmouth', 'panfish', 'sunfish'],
    bay: ['bay', 'estuary', 'inshore', 'halibut', 'spotted bay bass', 'sand bass', 'redfish', 'snook', 'flounder', 'croaker', 'seatrout'],
    harbor: ['harbor', 'bay', 'inshore', 'halibut', 'spotted bay bass', 'sand bass', 'croaker', 'mackerel', 'bonito'],
    pier: ['pier', 'surf', 'inshore', 'mackerel', 'bonito', 'croaker', 'surfperch', 'halibut', 'sand bass'],
    surf: ['surf', 'beach', 'surfperch', 'corbina', 'croaker', 'halibut', 'striped bass', 'pompano', 'bluefish'],
    inshore: ['inshore', 'bay', 'estuary', 'redfish', 'snook', 'tarpon', 'bonefish', 'halibut', 'mackerel', 'bonito', 'kelp bass'],
    ocean: ['ocean', 'coast', 'inshore', 'offshore', 'pelagic', 'tuna', 'mackerel', 'wahoo', 'mahi', 'cobia', 'billfish'],
    reef: ['reef', 'coral', 'bommie', 'rock', 'grouper', 'snapper', 'trevally', 'rabbitfish', 'emperor', 'jobfish'],
    backwater: ['backwater', 'estuary', 'lagoon', 'mangrove', 'canal', 'barramundi', 'pearlspot', 'karimeen', 'milkfish', 'threadfin'],
    estuary: ['estuary', 'bay', 'inshore', 'redfish', 'snook', 'flounder', 'seatrout', 'striped bass', 'salmon', 'steelhead'],
    marsh: ['marsh', 'estuary', 'redfish', 'black drum', 'flounder', 'speckled trout', 'snook'],
    canal: ['canal', 'bass', 'snook', 'carp', 'catfish', 'peacock bass'],
  };

  const needles = groups[waterType] || [waterType];
  return needles.some((needle) => hay.includes(needle));
};

const seasonFit = (species: any, ctx: FishIQLocationContext): boolean => {
  if (usesRegionalClimateSeason(ctx)) return false;
  const season = lower(ctx.season);
  if (!season) return false;
  return list(species?.bestSeasons).map(lower).includes(season) ||
    list(species?.speciesKnowledge?.seasonTags).map(lower).includes(season) ||
    list(species?.activityBySeason).some((row: any) => lower(row?.season) === season);
};

const timeFit = (species: any, ctx: FishIQLocationContext): boolean => {
  const time = lower(ctx.timeOfDay);
  if (!time) return false;
  return list(species?.bestTimesOfDay).map(lower).includes(time) ||
    list(species?.activeTimes).map(lower).includes(time) ||
    list(species?.speciesKnowledge?.activityWindows).map(lower).includes(time);
};

const activityWindowsFor = (species: any): string[] => unique([
  ...list(species?.bestTimesOfDay),
  ...list(species?.activeTimes),
  ...list(species?.speciesKnowledge?.activityWindows),
].map(lower).filter(Boolean)).slice(0, 4);

const buildBestActivityWindow = (species: any, ctx: FishIQLocationContext): string => {
  const windows = activityWindowsFor(species);
  if (!windows.length) return '';
  const now = lower(ctx.timeOfDay);
  if (now && windows.includes(now)) return `${humanize(now)} — active window`;
  return windows.slice(0, 2).map(humanize).join(' / ');
};

const findLocalSignal = (species: any, signals: any[] = []): any | null => {
  if (!Array.isArray(signals) || !signals.length) return null;
  const keys = new Set(getSpeciesKeys(species).map((x) => x.replace(/\s+/g, '_')));
  const plain = new Set(getSpeciesKeys(species));
  return signals.find((signal) => {
    const s = lower(signal?.speciesKey || signal?.species || signal?.commonName || signal?.name);
    const normalized = s.replace(/\s+/g, '_');
    return plain.has(s) || keys.has(normalized);
  }) || null;
};

const hasPersonalCatch = (species: any, catchLog: any[] = []): boolean => {
  if (!Array.isArray(catchLog) || !catchLog.length) return false;
  const keys = new Set(getSpeciesKeys(species));
  return catchLog.some((entry) => {
    const raw = lower(entry?.species || entry?.fishSpecies || entry?.commonName || entry?.speciesName || entry?.fishName);
    return raw && (keys.has(raw) || [...keys].some((key) => raw.includes(key) || key.includes(raw)));
  });
};

const bestBaitText = (species: any): string => {
  const bait = list(species?.bestBaits || species?.tactics?.bestBaits)[0];
  if (!bait) return '';
  if (typeof bait === 'string') return bait;
  return clean(bait?.name || bait?.label || bait?.bait || bait?.title);
};

const bestTechniqueText = (species: any): string => {
  const technique = list(species?.bestTechniques || species?.tactics?.bestTechniques)[0];
  if (!technique) return '';
  if (typeof technique === 'string') return technique;
  return clean(technique?.name || technique?.label || technique?.technique || technique?.title);
};

const firstStructure = (species: any): string => clean(list(species?.structureTags || species?.speciesKnowledge?.structureTags)[0]);

const baseLabelForBucket = (bucket: FishIQLocationRankBucket) => {
  switch (bucket) {
    case 'likely': return { label: 'Strong regional match', shortLabel: 'Region' };
    case 'good_target': return { label: 'Regional habitat match', shortLabel: 'Habitat' };
    case 'possible': return { label: 'Species guide', shortLabel: 'Guide' };
    case 'seasonal': return { label: 'Seasonal pattern', shortLabel: 'Seasonal' };
    case 'stocked': return { label: 'Stocked-water pattern', shortLabel: 'Stocked' };
    case 'quiet':
    default: return { label: 'Species guide', shortLabel: 'Guide' };
  }
};

const displayRegionalLabel = (label: string, bucket: FishIQLocationRankBucket) => {
  const l = lower(label);
  if (l.includes('stocked')) return { label: 'Stocked-water pattern', shortLabel: 'Stocked' };
  if (l.includes('seasonal')) return { label: 'Seasonal regional pattern', shortLabel: 'Seasonal' };
  if (l.includes('strong regional') || l.includes('known regional')) return { label: 'Strong regional match', shortLabel: 'Region' };
  if (l.includes('common regional')) return { label: 'Common regional target', shortLabel: 'Target' };
  if (l.includes('regional habitat') || l.includes('documented habitat')) return { label: 'Regional habitat match', shortLabel: 'Habitat' };
  if (l.includes('regional species guide')) return { label: 'Regional species guide', shortLabel: 'Guide' };
  return baseLabelForBucket(bucket);
};

const labelForBucket = (bucket: FishIQLocationRankBucket, regionalLabel?: string | null) => {
  if (regionalLabel) return displayRegionalLabel(regionalLabel, bucket);
  return baseLabelForBucket(bucket);
};

const sourceLabelFor = (regionalSignal: FishIQRegionalSpeciesSignal | null, localSignal: any | null, species: any): string => {
  if (localSignal) return 'Roddo local signal';
  if (regionalSignal) return 'Regional profile';
  if (Array.isArray(species?.publicSourceRefs) && species.publicSourceRefs.length) return 'Public source backed';
  return 'Roddo curated';
};

const confidenceToneFor = (
  bucket: FishIQLocationRankBucket,
  sourceLabel: string,
): FishIQLocationRank['confidenceTone'] => {
  if (sourceLabel === 'Roddo local signal') return 'local';
  if (bucket === 'stocked') return 'stocked';
  if (bucket === 'seasonal') return 'seasonal';
  if (bucket === 'likely') return 'range';
  if (bucket === 'good_target') return 'range';
  if (bucket === 'quiet') return 'quiet';
  return 'profile';
};

const buildWhyHere = (
  species: any,
  ctx: FishIQLocationContext,
  reasons: string[],
  regionalLabel?: string | null,
  localSignal?: any | null,
) => {
  const name = speciesName(species);
  const habitat = clean(species?.speciesKnowledge?.habitatFact || species?.summary || '');
  const water = humanize(ctx.waterType || ctx.waterKind || 'water');
  const count7d = Number(localSignal?.count7d || localSignal?.count24h || 0);
  const users7d = Number(localSignal?.uniqueUsers7d || localSignal?.uniqueUsers24h || 0);

  if (localSignal) {
    const volume = count7d > 0 && users7d > 0 ? ` (${count7d} delayed reads from ${users7d} anglers)` : '';
    return `${name} has a privacy-safe Roddo local signal near this ${water.toLowerCase()} context${volume}.`;
  }
  if (habitat) return habitat;
  if (regionalLabel) return `${name} has a ${humanize(regionalLabel).toLowerCase()} for this ${water.toLowerCase()} context.`;
  if (reasons.length) return `${name} matches ${reasons.slice(0, 2).join(' and ')} in this ${water.toLowerCase()} context.`;
  return `${name} is shown from the FishIQ species guide.`;
};

const buildWhyNow = (species: any, ctx: FishIQLocationContext) => {
  const seasonal = clean(species?.speciesKnowledge?.seasonalMovementFact || species?.speciesKnowledge?.seasonalFact || '');
  const windows = activityWindowsFor(species);
  const now = lower(ctx.timeOfDay);
  if (now && windows.includes(now)) {
    return `${humanize(now)} overlaps a general activity/feeding window for this species. Habitat, current, forage, weather, and local pressure still decide whether fish actually feed.`;
  }
  if (windows.length) {
    return `Better general activity windows are ${windows.slice(0, 2).map(humanize).join(' / ')}; use the current water, bait, and structure read to decide whether to stay or move.`;
  }
  if (seasonal) return seasonal;
  return clean(species?.speciesKnowledge?.timingFact || '');
};

const buildBestMove = (species: any, ctx: FishIQLocationContext) => {
  const tactic = clean(species?.speciesKnowledge?.tacticFact || bestTechniqueText(species));
  if (tactic) return tactic;
  const structure = firstStructure(species);
  const bait = bestBaitText(species);
  const parts = [structure, bait].filter(Boolean);
  if (parts.length) return `Start around ${parts.join(' with ')}.`;
  return `Start with habitat, forage, and seasonal movement for this ${humanize(ctx.waterType || 'water').toLowerCase()} profile.`;
};

const buildSupportingReads = (
  species: any,
  ctx: FishIQLocationContext,
  regionalSignal: FishIQRegionalSpeciesSignal | null,
  localSignal: any | null,
  reasons: string[],
) => {
  const rows = [
    ...(regionalSignal?.reasons || []),
    ...reasons,
    ...(localSignal
      ? [
          'Roddo local signal',
          Number(localSignal?.count7d || 0) > 0 ? `${Number(localSignal.count7d)} delayed local reads` : '',
          Number(localSignal?.uniqueUsers7d || 0) > 0 ? `${Number(localSignal.uniqueUsers7d)} contributing anglers` : '',
          ...(Array.isArray(localSignal?.topTechniques) ? localSignal.topTechniques.slice(0, 2) : []),
          ...(Array.isArray(localSignal?.topLures) ? localSignal.topLures.slice(0, 2) : []),
        ]
      : []),
    clean(species?.speciesKnowledge?.feedingFact),
    clean(species?.speciesKnowledge?.spawningFact),
    clean(species?.speciesKnowledge?.depthFact),
    clean(ctx.waterType ? `${humanize(ctx.waterType)} water context` : ''),
  ].filter(Boolean);
  return unique(rows).slice(0, 6);
};

const regionalContextFrom = (ctx: FishIQLocationContext): FishIQRegionalProfileContext => ({
  lat: ctx.lat,
  lon: ctx.lon,
  waterName: ctx.waterName,
  waterType: ctx.waterType,
  waterKind: ctx.waterKind,
  regionHint: ctx.regionHint,
  season: ctx.season,
});

const bucketFromScore = (score: number): FishIQLocationRankBucket => {
  if (score >= 80) return 'likely';
  if (score >= 58) return 'good_target';
  if (score >= 38) return 'possible';
  return 'quiet';
};

const environmentStructureLabel = (value: any) =>
  humanize(value || '').replace(/\bDrop Off\b/i, 'drop-off');

export type FishIQEnvironmentCompatibility = {
  compatible: boolean;
  currentCompatible: boolean;
  bathymetryCompatible: boolean;
  contextRealm: 'freshwater' | 'saltwater' | 'brackish' | 'unknown';
  speciesRealm: 'freshwater' | 'saltwater' | 'brackish' | 'both' | 'unknown';
  reason?: string;
};

const environmentSourceText = (environment: any): string =>
  [
    environment?.current?.sourceLabel,
    environment?.provider,
    ...(Array.isArray(environment?.provenance)
      ? environment.provenance.flatMap((row: any) => [
          row?.provider,
          row?.dataset,
          row?.sourceId,
        ])
      : []),
  ]
    .map(lower)
    .filter(Boolean)
    .join(' ');

const environmentHasMarineOfsEvidence = (environment: any): boolean => {
  const source = environmentSourceText(environment);
  return /\b(wcofs|dbofs|ciofs|gomofs|ngofs2?|tbofs)\b|ocean forecast system|ofs surface/.test(source);
};

const speciesRealmForEnvironment = (species: any): FishIQEnvironmentCompatibility['speciesRealm'] => {
  const category = lower(species?.category);
  const realms = list(species?.waterRealms).map(lower);
  const habitats = list(species?.habitats).map(lower).join(' ');
  const hay = `${category} ${realms.join(' ')} ${habitats} ${lower(species?.speciesKnowledge?.habitatFact)}`;

  const freshwater =
    category === 'freshwater' ||
    category === 'both' ||
    realms.includes('freshwater') ||
    realms.includes('anadromous') ||
    /lake|river|reservoir|pond|creek|stream|freshwater/.test(hay);
  const saltwater =
    category === 'saltwater' ||
    category === 'both' ||
    realms.includes('saltwater') ||
    realms.includes('anadromous') ||
    /ocean|offshore|inshore|coast|bay|harbor|surf|reef|sea|marine/.test(hay);
  const brackish =
    realms.includes('brackish') ||
    realms.includes('anadromous') ||
    /brackish|estuary|lagoon|delta|tidal|mangrove/.test(hay);

  if ((freshwater && saltwater) || category === 'both' || realms.includes('anadromous')) return 'both';
  if (brackish && !freshwater && !saltwater) return 'brackish';
  if (saltwater) return 'saltwater';
  if (freshwater) return 'freshwater';
  return 'unknown';
};

const contextRealmForEnvironment = (ctx: FishIQLocationContext, environment: any): FishIQEnvironmentCompatibility['contextRealm'] => {
  if (environmentHasMarineOfsEvidence(environment)) return 'saltwater';

  const resolved = resolveWaterContext({
    lat: ctx.lat,
    lon: ctx.lon,
    waterName: ctx.waterName,
    waterKind: ctx.waterKind,
    waterType: ctx.waterType,
    regionHint: ctx.regionHint,
  });
  const kind = lower(ctx.waterKind);
  const hay = lower([ctx.waterType, ctx.waterName, ctx.regionHint].filter(Boolean).join(' '));

  if (kind === 'brackish' || /\b(brackish|estuary|lagoon|delta|tidal)\b/.test(hay)) return 'brackish';
  if (kind === 'saltwater' || resolved.contextClass === 'coastal' || resolved.contextClass === 'offshore') return 'saltwater';
  if (kind === 'freshwater' || resolved.contextClass === 'freshwater' || resolved.contextClass === 'inland') return 'freshwater';
  return 'unknown';
};

const speciesSupportsDeepBathymetry = (species: any): boolean => {
  const hay = lower([
    species?.category,
    ...(list(species?.waterRealms)),
    ...(list(species?.habitats)),
    ...(list(species?.structureTags)),
    species?.depthPreference,
    species?.depthPatterns?.deep,
    species?.speciesKnowledge?.depthFact,
    species?.speciesKnowledge?.habitatFact,
    species?.commonName,
    species?.name,
  ].filter(Boolean).join(' '));

  return /offshore|ocean|pelagic|deep|shelf|slope|canyon|basin|rockfish|lingcod|halibut|tuna|wahoo|mahi|marlin|sailfish|swordfish|cod/.test(hay);
};

export function applyFishIQEnvironmentWaterContext(
  ctx: FishIQLocationContext,
  environment: any | null | undefined,
): FishIQLocationContext {
  if (!environment || !environmentHasMarineOfsEvidence(environment)) return ctx;

  const resolvedBeforeEnvironment = resolveWaterContext({
    lat: ctx.lat,
    lon: ctx.lon,
    waterName: ctx.waterName,
    waterKind: ctx.waterKind,
    waterType: ctx.waterType,
    regionHint: ctx.regionHint,
  });
  const existingKind = lower(ctx.waterKind);
  const existingType = lower(ctx.waterType);
  const existingText = lower([ctx.waterName, ctx.waterType, ctx.waterKind].filter(Boolean).join(' '));

  // Never let a nearby/nearest OFS grid cell turn an explicit inland read into
  // saltwater. This was the main source of coastal bass/halibut appearing in
  // rivers and inland waterways near LA/San Diego.
  if (
    existingKind === 'freshwater' ||
    existingKind === 'river' ||
    existingKind === 'lake' ||
    existingType === 'freshwater' ||
    existingType === 'river' ||
    existingType === 'lake' ||
    existingType === 'reservoir' ||
    resolvedBeforeEnvironment.contextClass === 'freshwater' ||
    resolvedBeforeEnvironment.contextClass === 'inland'
  ) {
    return ctx;
  }

  // Explicit estuary/brackish callers already carry the right mixed-water
  // semantics; do not flatten them to generic saltwater.
  if (existingKind === 'brackish' || existingType === 'brackish' || /\b(brackish|estuary|tidal|river mouth)\b/.test(existingText)) {
    return ctx;
  }

  const depth = Number(environment?.bathymetry?.depthMeters);
  const bathymetryAvailable = environment?.bathymetry?.available === true;
  const exactMarineBottom = bathymetryAvailable && Number.isFinite(depth) && depth > 0;
  const explicitMarineContext =
    existingKind === 'saltwater' ||
    resolvedBeforeEnvironment.contextClass === 'coastal' ||
    resolvedBeforeEnvironment.contextClass === 'offshore' ||
    /\b(ocean|sea|offshore|inshore|coast|coastal|marine|bay|harbor|harbour|marina|surf|beach|pier|jetty|reef)\b/.test(existingText);

  // Marine-current provider metadata is supportive, not authoritative. Promote
  // an unknown context only when the exact anchor also has bathymetry coverage,
  // or when the caller already supplied explicit marine context.
  if (!explicitMarineContext && !exactMarineBottom) return ctx;

  const preserveSpecificCoastalType = /bay|harbor|estuary|lagoon|reef|surf|pier|inshore/.test(existingType);
  const waterType = preserveSpecificCoastalType
    ? ctx.waterType
    : Number.isFinite(depth) && depth >= 100
      ? 'ocean'
      : 'inshore';

  return {
    ...ctx,
    waterKind: 'saltwater',
    waterType,
    source: ctx.source ? `${ctx.source}+water-intel` : 'water-intel',
  };
}

export function getFishIQEnvironmentCompatibility(
  species: any,
  context: Partial<FishIQLocationContext> = {},
  environment: any | null | undefined,
): FishIQEnvironmentCompatibility {
  const baseCtx = buildFishIQLocationContext(context);
  const ctx = applyFishIQEnvironmentWaterContext(baseCtx, environment);
  const contextRealm = contextRealmForEnvironment(ctx, environment);
  const speciesRealm = speciesRealmForEnvironment(species);

  const currentAvailable = environment?.current?.available === true;
  const bathymetryAvailable = environment?.bathymetry?.available === true;

  let realmCompatible = true;
  if (contextRealm === 'saltwater') {
    realmCompatible = speciesRealm === 'saltwater' || speciesRealm === 'brackish' || speciesRealm === 'both' || speciesRealm === 'unknown';
  } else if (contextRealm === 'freshwater') {
    realmCompatible = speciesRealm === 'freshwater' || speciesRealm === 'both' || speciesRealm === 'unknown';
  } else if (contextRealm === 'brackish') {
    realmCompatible = speciesRealm === 'brackish' || speciesRealm === 'saltwater' || speciesRealm === 'freshwater' || speciesRealm === 'both' || speciesRealm === 'unknown';
  }

  let currentCompatible = !currentAvailable || realmCompatible;
  let bathymetryCompatible = !bathymetryAvailable || realmCompatible;
  let reason = realmCompatible ? '' : `${speciesRealm} species does not match ${contextRealm} Water Intel`;

  const depth = Number(environment?.bathymetry?.depthMeters);
  if (bathymetryAvailable && bathymetryCompatible && Number.isFinite(depth) && contextRealm === 'saltwater') {
    const hay = lower([
      ...(list(species?.habitats)),
      ...(list(species?.structureTags)),
      species?.speciesKnowledge?.habitatFact,
      species?.depthPreference,
    ].filter(Boolean).join(' '));
    const shallowOnly = /bay|harbor|marsh|backwater|estuary|lagoon|surf|pier|shoreline|flats|mangrove/.test(hay) && !speciesSupportsDeepBathymetry(species);
    const deepUnsupported = depth >= 250 && !speciesSupportsDeepBathymetry(species);
    if (shallowOnly || deepUnsupported) {
      bathymetryCompatible = false;
      if (!reason) reason = `bottom depth ${Math.round(depth)} m is outside this species' tactical depth context`;
    }
  }

  const compatible =
    (!currentAvailable && !bathymetryAvailable) ||
    (currentAvailable && currentCompatible) ||
    (bathymetryAvailable && bathymetryCompatible);

  return {
    compatible,
    currentCompatible,
    bathymetryCompatible,
    contextRealm,
    speciesRealm,
    ...(reason ? { reason } : {}),
  };
}

const buildEnvironmentalAdjustment = (
  species: any,
  ctx: FishIQLocationContext,
  environment: any | null | undefined,
) => {
  if (!environment) return null;

  const compatibility = getFishIQEnvironmentCompatibility(species, ctx, environment);

  const confidence = Math.max(0, Math.min(1, Number(environment?.environmentConfidence || 0)));
  const speciesHay = textForSpecies(species);
  const reasons: string[] = [];
  const supportingReads: string[] = [];
  const whyNowParts: string[] = [];
  let scoreDelta = 0;
  let bestMove = '';

  const current = compatibility.currentCompatible ? environment?.current : null;
  if (current?.available) {
    const speed = Number(current?.speedKnots);
    const strength = lower(current?.strength || 'unknown');
    const direction = clean(current?.directionLabel);
    const phase = lower(current?.phase);
    const trend = lower(current?.trend);
    const currentText = Number.isFinite(speed)
      ? `${speed < 1 ? speed.toFixed(2) : speed.toFixed(1)} kt${direction ? ` ${direction}` : ''}`
      : 'Current available';
    supportingReads.push(currentText);

    if (strength === 'moderate' || strength === 'strong') {
      if (/current|river|channel|drift|seam|eddy|moving water|tidal/.test(speciesHay)) {
        scoreDelta += strength === 'strong' ? 4 : 3;
        reasons.push(`${humanize(strength)} current fit`);
      }
      whyNowParts.push(`${humanize(strength)} current${direction ? ` moving ${direction}` : ''} is part of this live water read.`);
    } else if (strength === 'slack' || phase === 'slack') {
      whyNowParts.push('Current is near slack at this read, so movement is reduced right now.');
    } else if (strength === 'light') {
      whyNowParts.push(`${currentText} gives this area light water movement right now.`);
    }

    if (trend && trend !== 'unknown') supportingReads.push(`Current ${trend}`);
  }

  const bathymetry = compatibility.bathymetryCompatible ? environment?.bathymetry : null;
  if (bathymetry?.available) {
    const depth = Number(bathymetry?.depthMeters);
    const relief = Number(bathymetry?.localReliefMeters);
    const slope = Number(bathymetry?.slopeDeg);
    const structures = Array.isArray(bathymetry?.structureTypes)
      ? bathymetry.structureTypes.filter(Boolean)
      : [];

    if (Number.isFinite(depth)) supportingReads.push(`${Math.round(depth)} m depth`);
    if (Number.isFinite(relief)) supportingReads.push(`${Math.round(relief)} m local relief`);
    if (Number.isFinite(slope)) supportingReads.push(`${slope.toFixed(1)}° slope`);

    const matchedStructure = structures.find((structure: any) => {
      const raw = lower(structure).replace(/_/g, ' ');
      const needles = raw === 'drop off'
        ? ['drop off', 'drop-off', 'ledge']
        : raw === 'channel edge'
          ? ['channel edge', 'channel', 'edge']
          : raw === 'basin edge'
            ? ['basin edge', 'basin', 'edge']
            : [raw];
      return needles.some((needle) => needle && speciesHay.includes(needle));
    });

    if (matchedStructure) {
      scoreDelta += 5;
      reasons.push(`${environmentStructureLabel(matchedStructure)} structure fit`);
    } else if (structures.length && /structure|rock|reef|ledge|drop|channel|ridge|shelf|hole|edge/.test(speciesHay)) {
      scoreDelta += 2;
      reasons.push('structure-oriented profile');
    }

    if (!bestMove && structures.length && Number.isFinite(depth)) {
      const primaryStructure = environmentStructureLabel(structures[0]);
      const currentStrength = lower(current?.strength);
      if (currentStrength === 'strong') {
        bestMove = `Start on the slower side of the ${primaryStructure.toLowerCase()} around ${Math.round(depth)} m and work the current break.`;
      } else if (current?.available && currentStrength !== 'slack') {
        bestMove = `Work the up-current edge of the ${primaryStructure.toLowerCase()} around ${Math.round(depth)} m and let the presentation sweep naturally.`;
      } else {
        bestMove = `Start on the ${primaryStructure.toLowerCase()} around ${Math.round(depth)} m and cover the depth change methodically.`;
      }
    } else if (!bestMove && Number.isFinite(depth) && current?.available) {
      const currentStrength = lower(current?.strength);
      const direction = clean(current?.directionLabel);
      if (currentStrength === 'strong') {
        bestMove = `At about ${Math.round(depth)} m, favor the slower side of the flow and keep the presentation controlled through the depth band.`;
      } else if (currentStrength !== 'slack') {
        bestMove = `At about ${Math.round(depth)} m, start up-current${direction ? ` of the ${direction.toLowerCase()} flow` : ""} and let the presentation sweep through the depth band.`;
      } else {
        bestMove = `At about ${Math.round(depth)} m with near-slack flow, cover the depth band methodically before moving.`;
      }
    }
  }

  const weatherSignal = environment?.weatherSignal || null;
  const condition = clean(environment?.weather?.condition);
  const weatherSpeciesText = lower([
    species?.summary,
    ...(list(species?.behaviorTags)),
    ...(list(species?.bestTimesOfDay)),
    ...(list(species?.activeTimes)),
    ...(list(species?.speciesKnowledge?.activityWindows)),
    ...(list(species?.speciesKnowledge?.timingFact)),
  ].filter(Boolean).join(' '));

  if (weatherSignal?.lowLight) {
    supportingReads.push('Reduced-light conditions');
    if (/dawn|dusk|night|low light|low-light|nocturnal|shade|ambush/.test(weatherSpeciesText)) {
      scoreDelta += 2;
      reasons.push('reduced-light activity fit');
      whyNowParts.push('Reduced light overlaps this species’ documented activity/ambush pattern.');
    } else {
      whyNowParts.push('Reduced light is part of the live weather read and may widen usable edge water.');
    }
  } else if (weatherSignal?.cloudClass === 'clear') {
    supportingReads.push('Bright/clear conditions');
  }

  if (weatherSignal?.windClass === 'strong') {
    supportingReads.push('Strong wind');
    whyNowParts.push('Strong wind makes protected, controllable water more important tactically.');
  } else if (weatherSignal?.windClass === 'breezy') {
    supportingReads.push('Working breeze');
    if (/pelagic|open water|surf|inshore|estuary|bay|harbor|bait/.test(speciesHay)) {
      reasons.push('wind-edge search cue');
      whyNowParts.push('A working breeze adds a useful wind-edge versus protected-side comparison.');
    }
  }

  if (weatherSignal?.precipitationActive) supportingReads.push('Active precipitation');
  if (weatherSignal?.visibilityReduced) supportingReads.push('Reduced visibility');

  const tideStation = clean(environment?.tides?.stationName);
  if (tideStation) supportingReads.push(`Tide context: ${tideStation}`);
  if (condition) supportingReads.push(condition);
  const moon = clean(environment?.light?.moonPhase);
  if (moon) supportingReads.push(`Moon: ${moon}`);

  // Environmental context refines ranking; it never creates species presence.
  scoreDelta = Math.max(-10, Math.min(10, Math.round(scoreDelta * Math.max(0.35, confidence || 0.35))));

  return {
    confidence,
    scoreDelta,
    reasons: unique(reasons).slice(0, 4),
    whyNow: unique(whyNowParts).join(' ') || undefined,
    bestMove: bestMove || undefined,
    supportingReads: unique(supportingReads).slice(0, 6),
    compatible: compatibility.compatible,
    currentCompatible: compatibility.currentCompatible,
    bathymetryCompatible: compatibility.bathymetryCompatible,
    compatibilityReason: compatibility.reason,
  };
};

const scoreSpecies = (
  species: any,
  ctx: FishIQLocationContext,
  opts: FishIQLocationRankOptions,
): FishIQRankedSpecies => {
  const reasons: string[] = [];
  let score = 8;
  
  const kind = lower(ctx.waterKind);
  const category = lower(species?.category);
  
  const resolvedWaterContext = resolveWaterContext({
    lat: ctx.lat,
    lon: ctx.lon,
    waterName: ctx.waterName,
    waterKind: ctx.waterKind,
    waterType: ctx.waterType,
    regionHint: ctx.regionHint,
  });
  
  const explicitCoastalContext =
    kind === 'saltwater' ||
    kind === 'brackish' ||
    resolvedWaterContext.contextClass === 'coastal' ||
    resolvedWaterContext.contextClass === 'offshore' ||
    /\b(bay|harbor|harbour|marina|estuary|lagoon|inshore|ocean|sea|surf|beach|pier|jetty|breakwater|coast|coastal|brackish|tidal)\b/.test(
      lower(ctx.waterType || ctx.waterName || ctx.regionHint),
    );
  
  const speciesWaterAllowed = fishIQSpeciesAllowedForWaterContext(species, {
    lat: ctx.lat,
    lon: ctx.lon,
    waterName: ctx.waterName,
    waterKind: ctx.waterKind,
    waterType: ctx.waterType,
    regionHint: ctx.regionHint,
  });
  const speciesRegionalAllowed = fishIQSpeciesAllowedForAuthoritativeRegion(species, {
    lat: ctx.lat,
    lon: ctx.lon,
    waterName: ctx.waterName,
    waterKind: ctx.waterKind,
    waterType: ctx.waterType,
    regionHint: ctx.regionHint,
    season: ctx.season,
  });

  const saltOnlyOutsideCoastalContext =
    category === 'saltwater' &&
    !explicitCoastalContext;
  
  const regionalSignal = !speciesWaterAllowed || !speciesRegionalAllowed || saltOnlyOutsideCoastalContext
    ? null
    : getFishIQRegionalSpeciesSignal(species, regionalContextFrom(ctx));
  
  if (regionalSignal) {
    score += regionalSignal.scoreBoost || 0;
    reasons.push(...(regionalSignal.reasons || []));
  }
  
  const blockedByWaterContext =
    !speciesWaterAllowed ||
    !speciesRegionalAllowed ||
    saltOnlyOutsideCoastalContext ||
    (
      kind === 'freshwater' &&
      category === 'saltwater' &&
      !/estuary|brackish|tidal/.test(lower(ctx.waterType || ctx.waterName))
    );

  const hasRealmFit = blockedByWaterContext ? false : waterRealmFit(species, ctx);
  const hasTypeFit = blockedByWaterContext ? false : waterTypeFit(species, ctx);
  const hasSeasonFit = seasonFit(species, ctx);
  const hasTimeFit = timeFit(species, ctx);
  const localSignal = findLocalSignal(species, opts.signals);
  const personalCatch = hasPersonalCatch(species, opts.catchLog);
  const canonicalMetadata = getCanonicalFishIQMetadata(species);
  const waterbodyProfile = findFishIQWaterbodyProfile({ lat: ctx.lat, lon: ctx.lon, waterName: ctx.waterName });
  const waterbodySignal = getFishIQWaterbodySpeciesSignal(waterbodyProfile, species);

  if (waterbodySignal?.kind === 'excluded') {
    score -= 140;
    reasons.push('waterbody exclusion');
  } else if (waterbodySignal) {
    // Waterbody profiles are generalized editorial/agency hints. They improve
    // ordering but cannot independently create a Strong local match.
    score += waterbodySignal.score;
    reasons.push(waterbodySignal.label);
  }

  const environmentalSignal = buildEnvironmentalAdjustment(species, ctx, opts.environment);
  if (environmentalSignal?.scoreDelta) {
    score += environmentalSignal.scoreDelta;
    reasons.push(...environmentalSignal.reasons);
  }

  const geoAdjustment = getFishIQCatchabilityGeoAdjustment(species, ctx);
  if (geoAdjustment.score) {
    score += geoAdjustment.score;
    if (geoAdjustment.reason) reasons.push(geoAdjustment.reason);
  }

  const habitatZones = new Set(canonicalMetadata.habitatZones || []);
  const contextType = lower(ctx.waterType);
  if ((contextType === 'harbor' || contextType === 'bay') && habitatZones.has('bay-harbor')) {
    score += 7;
    reasons.push('bay/harbor habitat fit');
  }
  if ((contextType === 'river' || contextType === 'creek') && habitatZones.has('flowing-water')) {
    score += 7;
    reasons.push('flowing-water habitat fit');
  }
  if ((contextType === 'lake' || contextType === 'reservoir' || contextType === 'pond') && habitatZones.has('stillwater')) {
    score += 7;
    reasons.push('stillwater habitat fit');
  }
  if (contextType === 'reef' && habitatZones.has('hard-structure')) {
    score += 9;
    reasons.push('reef/structure habitat fit');
  }
  if (contextType === 'backwater' && habitatZones.has('estuary-wetland')) {
    score += 9;
    reasons.push('backwater/estuary habitat fit');
  }
  if (contextType === 'surf' && habitatZones.has('surf')) {
    score += 7;
    reasons.push('surf habitat fit');
  }

  if (hasRealmFit) {
    score += 16;
    reasons.push(`${humanize(ctx.waterKind || 'water')} profile`);
  }
  if (hasTypeFit) {
    score += 18;
    reasons.push(`${humanize(ctx.waterType || 'water')} habitat`);
  }
  if (hasSeasonFit) {
    score += 12;
    reasons.push(`${humanize(ctx.season)} seasonal window`);
  }
  if (hasTimeFit) {
    score += 6;
    reasons.push(`${humanize(ctx.timeOfDay)} activity window`);
  }
  if (localSignal) {
    const activityScore = Number(localSignal?.activityScore || 0);
    const count7d = Number(localSignal?.count7d || localSignal?.count24h || 0);
    const uniqueUsers7d = Number(localSignal?.uniqueUsers7d || localSignal?.uniqueUsers24h || 0);
    const signalBoost = Math.min(28, 14 + Math.round(Math.max(0, activityScore) / 8));
    score += signalBoost;
    reasons.push('Roddo local signal');
    if (count7d > 0) reasons.push(`${count7d} delayed local reads`);
    if (uniqueUsers7d > 0) reasons.push(`${uniqueUsers7d} contributing anglers`);
  }
  if (personalCatch) {
    score += 14;
    reasons.push('your catch history');
  }

  const q = lower(opts.query || '');
  if (q) {
    const keys = getSpeciesKeys(species);
    if (keys.some((key) => key.includes(q) || q.includes(key))) {
      score += 10;
      reasons.push('search match');
    }
  }

  const sourceLabel = sourceLabelFor(regionalSignal, localSignal, species);
  if (sourceLabel === 'Public source backed') score += 3;
  if (sourceLabel === 'Roddo curated') score += 1;
  if (blockedByWaterContext) {
    score -= 140;
    reasons.push(speciesRegionalAllowed ? 'water-context guard' : 'regional-coast guard');
  }

  score = Math.max(0, Math.min(100, Math.round(score)));

  let bucket = bucketFromScore(score);

  const regionalLabel = regionalSignal?.label || null;
  const regionalLabelLower = lower(regionalLabel);
  if (regionalLabelLower.includes('stocked')) bucket = score >= 30 ? 'stocked' : bucket;
  if (regionalLabelLower.includes('seasonal')) bucket = score >= 30 ? 'seasonal' : bucket;
  if (bucket === 'likely' && !localSignal && !personalCatch && !((regionalLabelLower.includes('strong regional') || regionalLabelLower.includes('known regional')) && (hasTypeFit || hasRealmFit))) {
    bucket = 'good_target';
    score = Math.min(score, 72);
  }

  const labels = labelForBucket(bucket, regionalLabel);
  const ranked: FishIQRankedSpecies = {
    ...species,
    __fishiq: {
      ...(species?.__fishiq || {}),
      locationRank: {
        score,
        bucket,
        label: labels.label,
        shortLabel: labels.shortLabel,
        reasons: unique(reasons).slice(0, 8),
        confidenceTone: confidenceToneFor(bucket, sourceLabel),
        bestWindow: buildBestActivityWindow(species, ctx),
      },
      whyHere: buildWhyHere(species, ctx, reasons, regionalLabel, localSignal),
      whyNow: environmentalSignal?.whyNow
        ? [buildWhyNow(species, ctx), environmentalSignal.whyNow].filter(Boolean).join(' ')
        : buildWhyNow(species, ctx),
      bestMove: environmentalSignal?.bestMove || buildBestMove(species, ctx),
      bestBait: bestBaitText(species),
      sourceLabel,
      supportingReads: unique([
        ...buildSupportingReads(species, ctx, regionalSignal, localSignal, reasons),
        ...(environmentalSignal?.supportingReads || []),
      ]).slice(0, 8),
      regionalSignal,
      canonicalDataQuality: canonicalMetadata?.dataQuality || null,
      environmentalSignal,
      waterbodyProfile: waterbodyProfile ? { id: waterbodyProfile.id, name: waterbodyProfile.name, status: waterbodyProfile.status } : null,
    },
  };

  return ranked;
};

export function rankFishIQSpeciesForLocation<T = any>(
  speciesList: T[] = [],
  context: Partial<FishIQLocationContext> = {},
  options: FishIQLocationRankOptions = {},
): Array<FishIQRankedSpecies<T>> {
  const baseCtx = buildFishIQLocationContext(context);
  const ctx = applyFishIQEnvironmentWaterContext(baseCtx, options.environment);

  // D.6.2 hard regional eligibility gate.
  //
  // Do this BEFORE scoring and before locationRank.reasons are truncated for UI.
  // D.6.1 correctly marked known wrong-coast species with `regional-coast guard`,
  // but the guard reason was appended late and could be sliced out of the first
  // eight display reasons. The later safe-list filter therefore had no reliable
  // signal that the species was blocked, allowing a known Atlantic/Gulf fish to
  // re-enter a Pacific Area Read through the global-catalog fallback.
  //
  // D.6.3: when a specific regional pack exists for the resolved water type,
  // it is authoritative for the primary Area Read. Generic/global species may
  // only fill reads where no specific regional authority is available.
  const regionalEligibleSpecies = (Array.isArray(speciesList) ? speciesList : [])
    .filter(Boolean)
    .filter((species: any) => fishIQSpeciesAllowedForAuthoritativeRegion(species, {
      lat: ctx.lat,
      lon: ctx.lon,
      waterName: ctx.waterName,
      waterKind: ctx.waterKind,
      waterType: ctx.waterType,
      regionHint: ctx.regionHint,
      season: ctx.season,
    }));

  const ranked = regionalEligibleSpecies
    .map((species: any) => scoreSpecies(applyFishIQCatchabilityFacts(species), ctx, options) as FishIQRankedSpecies<T>);

  const sortRanked = (items: Array<FishIQRankedSpecies<T>>) => items.sort((a: any, b: any) => {
    const scoreA = Number(a?.__fishiq?.locationRank?.score || 0);
    const scoreB = Number(b?.__fishiq?.locationRank?.score || 0);
    if (scoreB !== scoreA) return scoreB - scoreA;
    return speciesName(a).localeCompare(speciesName(b));
  });

  const safeRanked = ranked.filter((item: any) => {
    const reasons = item?.__fishiq?.locationRank?.reasons || [];
    return !reasons.some((reason: any) => {
      const normalized = String(reason || '').toLowerCase();
      return normalized === 'water-context guard' || normalized === 'regional-coast guard';
    });
  });

  // Area Read fallback can pass the global catalog when the water-biased list is
  // sparse. Do not merely bury blocked coastal-only fish at score 0 for explicit
  // freshwater/inland reads, because the UI consumes the first ~16 ranked items.
  // If there are safe alternatives, return those only. This preserves count where
  // the catalog supports it while keeping a last-resort escape for truly tiny test
  // seeds.
  const resolved = resolveWaterContext({
    lat: ctx.lat,
    lon: ctx.lon,
    waterName: ctx.waterName,
    waterKind: ctx.waterKind,
    waterType: ctx.waterType,
    regionHint: ctx.regionHint,
  });
  const explicitFreshwaterRead = resolved.contextClass === 'freshwater' || resolved.contextClass === 'inland' || lower(ctx.waterKind) === 'freshwater';
  // Never reintroduce water-blocked species just to fill a list. A shorter safe
  // list is more trustworthy than a full list containing impossible matches.
  if (safeRanked.length > 0) return sortRanked(safeRanked);

  // Never revive a species that failed a specific U.S. coastal-basin guard just
  // to fill the card. Unknown/unprofiled species may still flow through normal
  // ranking, but a known wrong-coast match stays out.
  const withoutRegionalBlocks = ranked.filter((item: any) => {
    const reasons = item?.__fishiq?.locationRank?.reasons || [];
    return !reasons.some((reason: any) => String(reason || '').toLowerCase() === 'regional-coast guard');
  });
  return sortRanked(withoutRegionalBlocks);
}

export function getFishIQRankBucketSummary(items: any[] = []) {
  const summary = {
    likely: 0,
    good_target: 0,
    possible: 0,
    seasonal: 0,
    stocked: 0,
    quiet: 0,
    total: 0,
  };

  for (const item of Array.isArray(items) ? items : []) {
    const bucket = item?.__fishiq?.locationRank?.bucket as FishIQLocationRankBucket | undefined;
    if (bucket && bucket in summary) summary[bucket] += 1;
    else summary.quiet += 1;
    summary.total += 1;
  }

  return summary;
}
